import React, { useContext, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UserContext from "context/UserContext";
import performRequest from "network/perform-request";
import { organizationMemberUserLinkRoute, searchOrganizationMemberUserRoute } from "network/api-routes";
import { useSnackbar } from "notistack";

const requestErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const UserSummary = ({ user }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
    <Avatar src={user.full_profile_pic_url} alt={user.full_name} sx={{ width: 52, height: 52 }} />
    <Box minWidth={0}>
      <Typography fontWeight={600}>{user.full_name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {user.cellphone_number || "Sin teléfono"} · {user.document_number || "Sin documento"}
      </Typography>
    </Box>
  </Stack>
);

const MemberUserLinkPanel = ({ organizationId, member, onChanged }) => {
  const { user: authUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [showSearch, setShowSearch] = useState(!member.user);
  const [searchPhone, setSearchPhone] = useState(member.user ? "" : member.cellphone_number || "");
  const [searchDocument, setSearchDocument] = useState(member.user ? "" : member.document_number || "");
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const linkedUser = member.user;
  const hasSearchCriteria = Boolean(searchPhone.trim() || searchDocument.trim());

  const resetSearch = (visible = !linkedUser) => {
    setShowSearch(visible);
    setSearchResult(null);
    setSearchError("");
    setSearchPhone(visible && !linkedUser ? member.cellphone_number || "" : "");
    setSearchDocument(visible && !linkedUser ? member.document_number || "" : "");
  };

  const beginReplacement = () => {
    setShowSearch(true);
    setSearchResult(null);
    setSearchError("");
    setSearchPhone("");
    setSearchDocument("");
  };

  const searchUser = () => {
    if (!hasSearchCriteria || searching) return;

    setSearching(true);
    setSearchResult(null);
    setSearchError("");

    performRequest(
      "POST",
      searchOrganizationMemberUserRoute(organizationId, member.id),
      {
        cellphone_number: searchPhone.trim() || null,
        document_number: searchDocument.trim() || null,
      },
      true,
      authUser,
    )
      .then((response) => setSearchResult(response.data.data))
      .catch((error) => {
        setSearchError(requestErrorMessage(error, "No se pudo buscar el usuario Reva."));
      })
      .finally(() => setSearching(false));
  };

  const updateLink = () => {
    if (!searchResult) return;

    setSubmitting(true);
    performRequest(
      "PUT",
      organizationMemberUserLinkRoute(organizationId, member.id),
      {
        user_id: searchResult.id,
        expected_current_user_id: linkedUser?.id ?? null,
      },
      true,
      authUser,
    )
      .then((response) => {
        onChanged(response.data.data);
        enqueueSnackbar(response.data.message, { variant: "success" });
        setConfirmation(null);
        resetSearch(false);
      })
      .catch((error) => {
        setConfirmation(null);
        setSearchError(requestErrorMessage(error, "No se pudo vincular el usuario."));
      })
      .finally(() => setSubmitting(false));
  };

  const unlinkUser = () => {
    if (!linkedUser) return;

    setSubmitting(true);
    performRequest(
      "DELETE",
      organizationMemberUserLinkRoute(organizationId, member.id),
      { expected_current_user_id: linkedUser.id },
      true,
      authUser,
    )
      .then((response) => {
        onChanged(response.data.data);
        enqueueSnackbar(response.data.message, { variant: "success" });
        setConfirmation(null);
        setShowSearch(true);
        setSearchPhone(member.cellphone_number || "");
        setSearchDocument(member.document_number || "");
      })
      .catch((error) => {
        setConfirmation(null);
        enqueueSnackbar(requestErrorMessage(error, "No se pudo desvincular el usuario."), { variant: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchUser();
    }
  };

  return (
    <Stack spacing={2}>
      {linkedUser && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <UserSummary user={linkedUser} />
          <Stack direction="row" spacing={1}>
            {!showSearch && (
              <Button variant="outlined" startIcon={<LinkOutlinedIcon />} onClick={beginReplacement}>
                Reemplazar
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffOutlinedIcon />}
              onClick={() => setConfirmation("unlink")}
            >
              Desvincular
            </Button>
          </Stack>
        </Stack>
      )}

      {showSearch && (
        <Stack spacing={1.5}>
          {linkedUser && (
            <Alert severity="info">
              Buscá la cuenta que reemplazará a {linkedUser.full_name}. El historial del miembro no será modificado.
            </Alert>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Teléfono del usuario Reva"
              value={searchPhone}
              onChange={(event) => setSearchPhone(event.target.value)}
              onKeyDown={onSearchKeyDown}
              disabled={searching || submitting}
            />
            <TextField
              fullWidth
              size="small"
              label="Documento del usuario Reva"
              value={searchDocument}
              onChange={(event) => setSearchDocument(event.target.value)}
              onKeyDown={onSearchKeyDown}
              disabled={searching || submitting}
            />
            <Button
              variant="contained"
              startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchOutlinedIcon />}
              onClick={searchUser}
              disabled={!hasSearchCriteria || searching || submitting}
              sx={{ flexShrink: 0 }}
            >
              Buscar
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Podés usar cualquiera de los dos datos. Si completás ambos, deben corresponder a la misma cuenta.
          </Typography>

          {searchError && <Alert severity="warning">{searchError}</Alert>}

          {searchResult && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <UserSummary user={searchResult} />
                {searchResult.link_status === "current" ? (
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    Ya está vinculado
                  </Typography>
                ) : searchResult.linkable ? (
                  <Button variant="contained" onClick={() => setConfirmation("link")} disabled={submitting}>
                    {linkedUser ? "Reemplazar usuario" : "Vincular usuario"}
                  </Button>
                ) : null}
              </Stack>
              {!searchResult.linkable && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Esta cuenta ya está vinculada a {searchResult.linked_member?.name || "otro miembro"} en la
                  organización.
                </Alert>
              )}
            </Paper>
          )}

          {linkedUser && (
            <Stack direction="row" justifyContent="flex-end">
              <Button onClick={() => resetSearch(false)} disabled={submitting}>
                Cancelar reemplazo
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <Dialog open={Boolean(confirmation)} onClose={() => !submitting && setConfirmation(null)} fullWidth maxWidth="sm">
        <DialogTitle>{confirmation === "unlink" ? "Desvincular usuario" : linkedUser ? "Reemplazar usuario" : "Vincular usuario"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmation === "unlink"
              ? `${linkedUser?.full_name} dejará de representar a este miembro en la app de Reva. El historial del miembro se conservará.`
              : linkedUser
                ? `Se reemplazará a ${linkedUser.full_name} por ${searchResult?.full_name}. El usuario anterior perderá acceso a este perfil de miembro.`
                : `${searchResult?.full_name} quedará vinculado a este miembro y podrá representarlo en la app de Reva.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(null)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={confirmation === "unlink" ? "error" : "primary"}
            onClick={confirmation === "unlink" ? unlinkUser : updateLink}
            disabled={submitting}
          >
            {submitting ? "Procesando…" : confirmation === "unlink" ? "Desvincular" : linkedUser ? "Reemplazar" : "Vincular"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MemberUserLinkPanel;
